import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Sparkles, Zap, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { experiencesApi } from '@/api';

const EventPromotionModal = ({ event, isOpen, onClose, onSuccess }) => {
  const { i18n } = useTranslation();
  const [selectedLevel, setSelectedLevel] = useState('essential');
  const [loading, setLoading] = useState(false);

  const levels = [
    {
      id: 'essential',
      name: i18n.language === 'fr' ? 'Essentiel' : 'Essential',
      price: 20,
      duration: i18n.language === 'fr' ? '7 jours' : '7 days',
      features: [
        i18n.language === 'fr' ? 'Badge "À la une" sur votre événement' : '"Featured" badge on your event',
        i18n.language === 'fr' ? 'Position prioritaire dans la section événements' : 'Priority position in events section',
        i18n.language === 'fr' ? 'Visible sur la page d\'accueil' : 'Visible on homepage',
      ],
      icon: Star,
      color: 'from-orange-400 to-amber-500'
    },
    {
      id: 'visibility',
      name: i18n.language === 'fr' ? 'Visibilité+' : 'Visibility+',
      price: 50,
      duration: i18n.language === 'fr' ? '14 jours' : '14 days',
      features: [
        i18n.language === 'fr' ? 'Tous les avantages Essentiel' : 'All Essential benefits',
        i18n.language === 'fr' ? 'Mise en avant dans les emails aux voyageurs' : 'Featured in traveler emails',
        i18n.language === 'fr' ? 'Position #1 garantie' : 'Guaranteed #1 position',
        i18n.language === 'fr' ? 'Badge premium exclusif' : 'Exclusive premium badge',
      ],
      icon: Sparkles,
      color: 'from-purple-500 to-pink-500',
      recommended: true
    }
  ];

  const handlePromote = async () => {
    setLoading(true);
    try {
      const result = await experiencesApi.promoteEvent(event.experience_id, selectedLevel);
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      console.error('Promotion error:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la promotion' : 'Error promoting event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="event-promotion-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-6 h-6 text-orange-500" />
            {i18n.language === 'fr' ? 'Mettre en avant votre événement' : 'Promote your event'}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          <p className="text-gray-600 mb-6">
            {i18n.language === 'fr' 
              ? `Boostez la visibilité de "${event?.title}" et attirez plus de participants !`
              : `Boost the visibility of "${event?.title}" and attract more participants!`}
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {levels.map((level) => {
              const Icon = level.icon;
              const isSelected = selectedLevel === level.id;
              
              return (
                <div
                  key={level.id}
                  onClick={() => setSelectedLevel(level.id)}
                  className={`relative cursor-pointer rounded-2xl p-6 border-2 transition-all ${
                    isSelected 
                      ? 'border-orange-500 bg-orange-50 shadow-lg scale-[1.02]' 
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                  data-testid={`promotion-level-${level.id}`}
                >
                  {level.recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {i18n.language === 'fr' ? 'Recommandé' : 'Recommended'}
                    </div>
                  )}

                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${level.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="text-xl font-bold mb-1">{level.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-3xl font-bold text-orange-600">{level.price}€</span>
                    <span className="text-gray-500">/ {level.duration}</span>
                  </div>

                  <ul className="space-y-2 mt-4">
                    {level.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {isSelected && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handlePromote} 
              disabled={loading}
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
              data-testid="promote-event-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  {i18n.language === 'fr' ? 'Chargement...' : 'Loading...'}
                </span>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  {i18n.language === 'fr' ? 'Promouvoir maintenant' : 'Promote now'}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventPromotionModal;
